import { Component, OnDestroy, OnInit, Output, EventEmitter, Input, OnChanges } from '@angular/core';
import { ControlContainer, UntypedFormBuilder, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { DisplayMode } from 'cbc-shared-lib';
import { SlideadjustmentunitpriceInfo } from 'sg-api';
import { ActivatedRoute } from '@angular/router';

/**
 * 再エネ賦課金単価登録画面(再エネ賦課金単価)
 * @exports
 * @class Sgkc034dReEnergyTankaInfoComponent
 * @implements {OnInit, OnDestroy, OnChanges}
 */
@Component({
    selector: 'app-sgkc034d-re-energy-tanka-info',
    templateUrl: './sgkc034d-re-energy-tanka-info.component.html',
    styleUrl: './sgkc034d-re-energy-tanka-info.component.scss',
    providers: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgkc034dReEnergyTankaInfoComponent implements OnInit, OnDestroy, OnChanges {
    /** 親コンポーネント保持領域 */
    parentForm: UntypedFormGroup;
    /** スライド調整単価情報 */
    @Input() slideadjustmentunitpriceInfo: SlideadjustmentunitpriceInfo;
    /** 画面モード */
    @Input() mode: string;
    /** 登録ボタン押下イベント */
    @Output() onClickRegistrationEvent = new EventEmitter<void>();
    /** 取消ボタン押下イベント */
    @Output() onClickDeleteEvent = new EventEmitter<void>();
    /** 承認ボタン押下イベント */
    @Output() onClickApprovalEvent = new EventEmitter<void>();

    /** 登録ボタン表示フラグFormGroup */
    isRegistVisible: boolean = false;
    /** 取消ボタン表示フラグFormGroup */
    isDeleteVisible: boolean = false;
    /** 承認ボタン表示フラグFormGroup */
    isApproveVisible: boolean = false;

    /** 数値の最大桁数(読取専用) */
    readonly constNumberCommaMaxLength: number = 5;
    /** 数値の最大桁数 */
    numberCommaMaxLength: number = this.constNumberCommaMaxLength;
    /** 適用年度 */
    applyYear = this.fb.control({ value: '', disabled: true }, Validators.required);
    /** 再エネ賦課金単価 */
    slideadjustmentunitprice = this.fb.control({ value: '', disabled: true }, Validators.required,);

    /** 再エネ賦課金Form */
    reEnergyTankaResultForm: UntypedFormGroup = this.fb.group({
        /** 適用年度(required) */
        applyYear: this.applyYear,
        /** 再エネ賦課金単価(required) */
        slideadjustmentunitprice: this.slideadjustmentunitprice,
    });

    /** 
    * コンストラクタ
    * @param {FormGroupDirective} parent 
    * @param {UntypedFormBuilder} fb 
    * @param {ActivatedRoute} route 
    * @memberof Sgkc034dReEnergyTankaInfoComponent
    */
    constructor(
        private parent: FormGroupDirective,
        private fb: UntypedFormBuilder,
        private route: ActivatedRoute,) {
    }

    /** 
    * 初期処理
    * @memberof Sgkc034dReEnergyTankaInfoComponent
    */
    ngOnInit(): void {

        this.parentForm = this.parent.form;
        this.parentForm.addControl('reEnergyTankaResultForm', this.reEnergyTankaResultForm);
        this.initializeDisplay();
        // 画面モードに応じてフォームとボタンの制御
        if (this.mode === DisplayMode.create) {
            this.reEnergyTankaResultForm.controls['slideadjustmentunitprice'].enable();
            this.isRegistVisible = true;
        } else if (this.mode === DisplayMode.delete) {
            this.isDeleteVisible = true;
        } else if (this.mode === DisplayMode.approve) {
            this.isApproveVisible = true;
        }
    }

    /**
     * コンポーネント破棄時の処理
     * @memberof Sgkc034dReEnergyTankaInfoComponent
     */
    ngOnDestroy(): void {
        if (this.parent.form != null) {
            this.parentForm.clearValidators();
            this.parentForm.updateValueAndValidity();
            this.parent.form.removeControl('reEnergyTankaResultForm');
        }
    }

    /**
     * 入力値の変更検知
     * @param {any} changes 
     * @memberof Sgkc034dReEnergyTankaInfoComponent
     */
    ngOnChanges(changes) {
        if (changes.slideadjustmentunitpriceInfo) {
            this.initializeDisplay();
        }
    }

    /**
     *  登録ボタン押下処理
     * @memberof Sgkc034dReEnergyTankaInfoComponent
     */
    onClickRegistration(): void {
        this.onClickRegistrationEvent.emit();
    }

    /** 
     * 承認ボタン押下処理 
     * @memberof Sgkc034dReEnergyTankaInfoComponent
     */
    onClickApproval(): void {
        this.onClickApprovalEvent.emit();
    }

    /**
     * 取消ボタン押下処理
     * @memberof Sgkc034dReEnergyTankaInfoComponent
     */
    onClickDelete(): void {
        this.onClickDeleteEvent.emit();
    }

    /**
     * 画面表示初期化処理
     * @memberof Sgkc034dReEnergyTankaInfoComponent
     */
    initializeDisplay(): void {
        // 再エネ賦課金FormGroupに初期値を設定
        // 登録の場合、スライド調整単価詳細取得で取得した適用年度に１加算した値を設定する。
        if (this.mode === DisplayMode.create) {
            this.reEnergyTankaResultForm.controls['applyYear'].setValue(String(Number(this.slideadjustmentunitpriceInfo.applyStartyearmonth) + 1));
            this.reEnergyTankaResultForm.controls['slideadjustmentunitprice'].setValue("");
        } else {
            this.reEnergyTankaResultForm.controls['applyYear'].setValue(this.slideadjustmentunitpriceInfo.applyStartyearmonth);
            this.reEnergyTankaResultForm.controls['slideadjustmentunitprice'].setValue(this.slideadjustmentunitpriceInfo.slideadjustmentunitprice !== null ? this.slideadjustmentunitpriceInfo.slideadjustmentunitprice.toFixed(2) : "");
        }
    }

    /**
     * フォーカスアウト時の処理
     * @remark 数部が2桁以下のとき2桁まで2桁以上のときそのまま表示
     * @memberof Sgkc034dReEnergyTankaInfoComponent
     */
    onBlurNumber(): void {
        // 画面入力項目を取得、前後の空白を削除
        let targetMoney: string = this.parentForm.get('reEnergyTankaResultForm.slideadjustmentunitprice').value.trim();
        if (targetMoney) {
            let numerTransValue = Number(targetMoney);
            // 数値のとき処理を実行
            if (!isNaN(numerTransValue)) {
                // 小数点以下の桁数を取得
                let decimalPlaces = (targetMoney.split('.')[1] || '').length;
                // 小数部が2桁以下のとき2桁まで表示、それ以上のときそのまま表示
                if (decimalPlaces <= 2) {
                    targetMoney = numerTransValue.toFixed(2);
                } else {
                    targetMoney = targetMoney;
                }
            }
        }
        setTimeout(() => {
            this.parentForm.get('reEnergyTankaResultForm.slideadjustmentunitprice').setValue(targetMoney);
        }, 1);
    }
}
